'use client';
import React, { useState } from 'react';
import { Search, ChevronUp, ChevronDown, Eye, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const mockBlocks = [
  { id: 'block-001', number: 2847294, owner: 'Amara Osei', email: 'amara.osei@gmail.com', category: 'finances', status: 'purchased', claimedAt: 'May 17, 2026', txId: 'txn_3Pq9xY2eZvKUdh8Q' },
  { id: 'block-002', number: 2847293, owner: 'Léa Fontaine', email: 'lea.f@outlook.fr', category: 'love', status: 'purchased', claimedAt: 'May 17, 2026', txId: 'txn_3Pq9xY1eZvKUdh7P' },
  { id: 'block-003', number: 2847292, owner: 'Rajan Mehta', email: 'rajan.mehta@proton.me', category: 'health', status: 'purchased', claimedAt: 'May 17, 2026', txId: 'txn_3Pq9xY0eZvKUdh6O' },
  { id: 'block-004', number: 2847291, owner: 'Sofia Reyes', email: 'sofia.r@icloud.com', category: 'peace', status: 'purchased', claimedAt: 'May 17, 2026', txId: 'txn_3Pq9xX9eZvKUdh5N' },
  { id: 'block-005', number: 2847290, owner: 'Kofi Asante', email: 'k.asante@gmail.com', category: 'career', status: 'purchased', claimedAt: 'May 16, 2026', txId: 'txn_3Pq9xX8eZvKUdh4M' },
  { id: 'block-006', number: 2847289, owner: 'Yuki Tanaka', email: 'yuki.t@yahoo.co.jp', category: 'family', status: 'purchased', claimedAt: 'May 16, 2026', txId: 'txn_3Pq9xX7eZvKUdh3L' },
  { id: 'block-007', number: 2847288, owner: 'Maria Santos', email: 'maria.santos@gmail.com', category: 'love', status: 'purchased', claimedAt: 'May 16, 2026', txId: 'txn_3Pq9xX6eZvKUdh2K' },
  { id: 'block-008', number: 2847287, owner: '—', email: '—', category: '—', status: 'available', claimedAt: '—', txId: '—' },
  { id: 'block-009', number: 2847286, owner: 'Ibrahim Al-Farsi', email: 'ibrahim.af@gmail.com', category: 'health', status: 'purchased', claimedAt: 'May 15, 2026', txId: 'txn_3Pq9xX5eZvKUdh1J' },
  { id: 'block-010', number: 2847285, owner: '—', email: '—', category: '—', status: 'available', claimedAt: '—', txId: '—' },
  { id: 'block-011', number: 2847284, owner: 'Chioma Eze', email: 'chioma.eze@hotmail.com', category: 'finances', status: 'purchased', claimedAt: 'May 15, 2026', txId: 'txn_3Pq9xX4eZvKUdh0I' },
  { id: 'block-012', number: 2847283, owner: 'Andrei Popescu', email: 'a.popescu@gmail.com', category: 'career', status: 'purchased', claimedAt: 'May 15, 2026', txId: 'txn_3Pq9xX3eZvKUdhzH' },
];

const categoryColors: Record<string, string> = {
  finances: 'text-primary bg-primary/10',
  love: 'text-pink-400 bg-pink-400/10',
  health: 'text-success bg-success/10',
  peace: 'text-fuchsia-300 bg-fuchsia-300/10',
  career: 'text-violet-400 bg-violet-400/10',
  family: 'text-blue-400 bg-blue-400/10',
};

export default function AdminBlocksTable() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string>('number');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const filtered = mockBlocks.filter(
    (b) =>
      b.number.toString().includes(search) ||
      b.owner.toLowerCase().includes(search.toLowerCase()) ||
      b.email.toLowerCase().includes(search.toLowerCase()) ||
      b.category.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const aVal = a[sortField as keyof typeof a];
    const bVal = b[sortField as keyof typeof b];
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const paginated = sorted.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: string }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <ChevronUp size={10} className={sortField === field && sortDir === 'asc' ? 'opacity-100 text-primary' : ''} />
      <ChevronDown size={10} className={sortField === field && sortDir === 'desc' ? 'opacity-100 text-primary' : ''} />
    </span>
  );

  return (
    <div className="cosmic-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h3 className="font-display font-semibold text-foreground">Block Registry</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} blocks shown · {mockBlocks.filter(b => b.status === 'purchased').length} total claimed</p>
        </div>
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search blocks, owners…"
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {[
                { field: 'number', label: 'Block #' },
                { field: 'owner', label: 'Owner' },
                { field: 'email', label: 'Email' },
                { field: 'category', label: 'Category' },
                { field: 'status', label: 'Status' },
                { field: 'claimedAt', label: 'Claimed At' },
                { field: 'txId', label: 'Transaction ID' },
              ].map((col) => (
                <th
                  key={`col-${col.field}`}
                  onClick={() => handleSort(col.field)}
                  className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap"
                >
                  {col.label}<SortIcon field={col.field} />
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((block, i) => (
              <tr
                key={block.id}
                className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
              >
                <td className="px-4 py-3 font-mono text-xs text-foreground font-semibold metric-number">#{block.number.toLocaleString()}</td>
                <td className="px-4 py-3 text-foreground whitespace-nowrap">{block.owner}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{block.email}</td>
                <td className="px-4 py-3">
                  {block.category !== '—' ? (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${categoryColors[block.category] || 'text-muted-foreground bg-muted'}`}>
                      {block.category}
                    </span>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    block.status === 'purchased' ? 'text-primary bg-primary/10' : 'text-success bg-success/10'
                  }`}>
                    {block.status === 'purchased' ? 'Claimed' : 'Available'}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{block.claimedAt}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[140px]">{block.txId}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button title="View block details" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
                      <Eye size={14} />
                    </button>
                    <button title="Edit block" className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-all">
                      <Edit2 size={14} />
                    </button>
                    <button
                      title="Delete block record"
                      onClick={() => toast.error('Block deletion requires confirmation')}
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-all"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-3 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length} blocks
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40 transition-all">
            <ChevronLeft size={15} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={`blk-page-${p}`} onClick={() => setPage(p)}
              className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${page === p ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'}`}>
              {p}
            </button>
          ))}
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground disabled:opacity-40 transition-all">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}